import useUser from 'hooks/useUser';
import Coupon from 'types/Coupon';
import PaymentMethod from 'types/PaymentMethod';
import PlanCode from 'types/PlanCode';
import PlanTypeType from 'types/PlanTypeType';
import { useEffect, useState } from 'react';
import ProPageLayout from 'components/ProPageLayout';
import PaymentForm from 'components/forms/pro/PaymentForm';
import logProCheckoutPageViewed from '@alltrails/analytics/events/logProCheckoutPageViewed';
import WebProPurchaseLocation from '@alltrails/analytics/enums/WebProPurchaseLocation';
import { ServerCommunicationUtil } from 'utils/server_communication_util';
import { defineMessages, FormattedMessage } from 'react-intl';
import CurrencyCode from 'types/CurrencyCode';
import useFormatMessage from '@alltrails/shared/hooks/useFormatMessage';

const messages = defineMessages({
  tryProFree: {
    defaultMessage: 'Try Pro free + subscribe'
  },
  getATPro: {
    defaultMessage: 'Get AllTrails Pro'
  }
});

type Props = {
  annualPlan: PlanTypeType;
  coupon?: Coupon;
  currencyCode: CurrencyCode;
  paymentTypes: PaymentMethod[];
  planTypes: PlanTypeType[];
  recurlyPublicKey: string;
  userId?: number;
};

export default function LegacyCheckout({
  annualPlan,
  recurlyPublicKey,
  planTypes: planTypesProp,
  paymentTypes,
  coupon,
  currencyCode,
  userId
}: Props) {
  const {
    formattedDefaultMessages: { tryProFree, getATPro }
  } = useFormatMessage(messages);

  useEffect(() => {
    logProCheckoutPageViewed();
  }, []);

  const user = useUser();

  const [isCouponInputExpanded, setIsCouponInputExpanded] = useState(!!coupon);
  const [couponCode, setCouponCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [verifiedCoupon, setVerifiedCoupon] = useState<Coupon>(coupon);
  const [isCouponApplied, setIsCouponApplied] = useState(!!coupon);
  const [couponError, setCouponError] = useState<React.ReactNode>();
  const [activePlanTypeAndPlanTypes, setActivePlanTypeAndPlanTypes] = useState<{ activePlanType: PlanCode; planTypes: PlanTypeType[] }>({
    activePlanType: planTypesProp[0].planCode,
    planTypes: planTypesProp
  });

  const { activePlanType, planTypes } = activePlanTypeAndPlanTypes;

  const submitText: string = activePlanType === PlanCode.SevenDayTrial ? tryProFree : getATPro;

  function handleRemoveCoupon() {
    setIsCouponInputExpanded(false);
    setCouponCode(null);
    setIsCouponApplied(false);
    setVerifiedCoupon(null);
  }

  function handleCouponCodeChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { value } = event.target;
    setCouponCode(value);
    setCouponError('');
  }

  function handleCouponVerification(couponCode: string) {
    if (submitting) {
      // Not ideal, but this will prevent double submission (until loading state is added)
      return;
    }

    setSubmitting(true);

    const data = {
      coupon_code: couponCode
    };

    ServerCommunicationUtil.postApiEndpoint(
      '/api/alltrails/coupons/verify',
      data,
      (data: Coupon[]) => {
        setVerifiedCoupon(data[0]);
        setIsCouponApplied(true);
        setSubmitting(false);

        // growth-868: force user from free trial to annual plan if they have a valid coupon
        setActivePlanTypeAndPlanTypes({ activePlanType: PlanCode.Annual, planTypes: [annualPlan] });
      },
      () => {
        setIsCouponApplied(false);
        setCouponError(<FormattedMessage defaultMessage="This coupon code is invalid" />);
        setSubmitting(false);
      },
      () => {}
    );
  }

  return (
    <ProPageLayout>
      <PaymentForm
        headText={<FormattedMessage defaultMessage="Almost Done!" />}
        submitText={submitText}
        recurlyPublicKey={recurlyPublicKey}
        activePlanType={activePlanType}
        planTypes={planTypes}
        handleApplyCoupon={() => handleCouponVerification(couponCode)}
        handleCouponCodeChange={handleCouponCodeChange}
        toggleCouponCode={() => setIsCouponInputExpanded(!isCouponInputExpanded)}
        handleRemoveCoupon={handleRemoveCoupon}
        isCouponApplied={isCouponApplied}
        verifiedCoupon={verifiedCoupon}
        couponError={couponError} // Coupon Codes use a different error messaging system since they verify when applying
        isCouponInputExpanded={isCouponInputExpanded}
        isCouponInputShown
        updateActivePlan={(activePlanType: PlanCode) => {
          setActivePlanTypeAndPlanTypes({ activePlanType, planTypes });
        }}
        paymentTypes={paymentTypes}
        couponCode={coupon?.code}
        currencyCode={currencyCode}
        webProPurchaseLocation={WebProPurchaseLocation.ProPage}
        userId={user ? user.id : userId}
      />
    </ProPageLayout>
  );
}
